import { Injectable, OnDestroy } from '@angular/core';
import { Client, IMessage, StompConfig, StompSubscription } from '@stomp/stompjs';
import { environment } from 'environments/environment';
import { BehaviorSubject, Subject } from 'rxjs';
import SockJS from 'sockjs-client';

export enum ConnectionStatus {
  CONNECTING = 'CONNECTING',
  DISCONNECTED = 'DISCONNECTED',
  CONNECTED = 'CONNECTED',
  RECONNECTING = 'RECONNECTING',
  ERROR = 'ERROR',
  CLOSED = 'CLOSED',
}
@Injectable({
  providedIn: 'root',
})
export class WebSocketService implements OnDestroy {
  private stompClient: Client | null = null;
  private readonly subscriptions: Map<string, StompSubscription> = new Map();
  private reconnectAttempts = 0;
  private readonly reconnectTimer: any = null;

  // Etat de la connexion
  private readonly connectionStatusSubject = new BehaviorSubject<ConnectionStatus>(
    ConnectionStatus.DISCONNECTED
  );
  public connectionStatus$ = this.connectionStatusSubject.asObservable();

  // Evenements de connexion
  private readonly connectedSubject = new Subject<void>();
  public connected$ = this.connectedSubject.asObservable();

  private readonly disconnectedSubject = new Subject<void>();
  public disconnected$ = this.disconnectedSubject.asObservable();

  private readonly errorSubject = new Subject<string>();
  public error$ = this.errorSubject.asObservable();

  constructor() {
    console.log('WebSocketService initialized');
  }

  // se connect au websocket
  connect(userId: string, token?: string): void {
    if (this.stompClient?.active) {
      console.log('WebSocket is already connected');
      return;
    }

    console.log(`Connecting to WebSocket as user ${userId}...`);
    this.connectionStatusSubject.next(ConnectionStatus.CONNECTING);

    // configuration stomp
    const stompConfig: StompConfig = {
      // Utiliser SockJS comme transport
      webSocketFactory: () => new SockJS(environment.api.wslUrl) as any,

      // Headers de connection (JWT token)
      connectHeaders: token
        ? {
            Authorization: `Bearer ${token}`,
          }
        : {},
      // HeatBeat (ping/pong)
      heartbeatIncoming: 10000, // 10 secondes
      heartbeatOutgoing: 10000, // 10 secondes

      // reconexion automatique
      reconnectDelay: environment.notification.wsReconnectInterval,

      // Debug (désactiver en production)
      debug: (str) => {
        if (!environment.production) {
          console.log('STOMP:', str);
        }
      },

      // Callbacks
      onConnect: () => this.onConnected(userId),
      onDisconnect: () => this.onDisconnected(),
      onStompError: (frame) => this.onError(frame),
      onWebSocketError: (event) => this.onWebSocketError(event),
    };

    // créer le client stomp
    this.stompClient = new Client(stompConfig);

    // démarrer la connexion
    this.stompClient.activate();
  }

  // se deconnecter du websocket
  disconnect(): void {
    if (!this.stompClient) {
      return;
    }
    console.log('Disconnecting from WebSocket...');

    // nettoyage les souscriptions
    for (const [id, sub] of this.subscriptions) {
      sub.unsubscribe();
      this.subscriptions.delete(id);
    }
    this.subscriptions.clear();

    // nettoyer le timer de reconnexion
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    // desactiver le client
    this.stompClient.deactivate();
    this.stompClient = null;

    this.connectionStatusSubject.next(ConnectionStatus.DISCONNECTED);
  }

  // Souscrire a un channel
  subscribe<T>(destination: string, callback: (message: T) => void): string {
    if (!this.stompClient?.connected) {
      throw new Error('WebSocket is not connected');
    }

    console.log(`📡 Subscribing to ${destination}...`);
    const subscription = this.stompClient.subscribe(destination, (message: IMessage) => {
      console.log(`📬 Message received on ${destination}:`, message.body);
      try {
        const data = JSON.parse(message.body) as T;
        console.log(`Parsed message:`, data);
        callback(data);
      } catch (error) {
        console.error('Error processing message:', error);
      }
    });
    const subscriptionId = this.generateSubscriptionId();
    this.subscriptions.set(subscriptionId, subscription);
    console.log(`✅ Successfully subscribed to ${destination} (ID: ${subscriptionId})`);

    return subscriptionId;
  }

  // se désabonner d'un channel
  unsubscribe(subscriptionId: string): void {
    const subscription = this.subscriptions.get(subscriptionId);
    if (subscription) {
      subscription.unsubscribe();
      this.subscriptions.delete(subscriptionId);
      console.log(`Unsubscribed from ${subscriptionId}`);
    }
  }

  // envoyer message au serveur
  send<T>(destination: string, body: T): void {
    if (!this.stompClient?.connected) {
      throw new Error('WebSocket is not connected');
    }

    console.log(`Sending message to ${destination}:`, body);

    this.stompClient.publish({
      destination,
      body: JSON.stringify(body),
    });
  }

  // verifier si tu es connecter
  isConnected(): boolean {
    return this.stompClient?.connected ?? false;
  }

  // obtenir le status de la connexion
  getConnectionStatus(): ConnectionStatus {
    return this.connectionStatusSubject.value;
  }

  // ========== CALLBACKS ==========

  /**
   * Callback : Connexion établie
   */
  private onConnected(userId: string): void {
    console.log(`✅ WebSocket connected for user ${userId}`);

    this.reconnectAttempts = 0;
    this.connectionStatusSubject.next(ConnectionStatus.CONNECTED);
    this.connectedSubject.next();
  }

  /**
   * Callback : Déconnexion
   */
  private onDisconnected(): void {
    console.log('🔌 WebSocket disconnected');

    this.connectionStatusSubject.next(ConnectionStatus.DISCONNECTED);
    this.disconnectedSubject.next();

    // Tenter reconnexion si nécessaire
    this.attemptReconnect();
  }

  /**
   * Callback : Erreur STOMP
   */
  private onError(frame: any): void {
    console.error('STOMP error:', frame);

    this.connectionStatusSubject.next(ConnectionStatus.ERROR);
    this.errorSubject.next(frame.headers?.message || 'STOMP error');

    // Tenter reconnexion
    this.attemptReconnect();
  }

  /**
   * Callback : Erreur WebSocket
   */
  private onWebSocketError(event: any): void {
    console.error('WebSocket error:', event);

    this.connectionStatusSubject.next(ConnectionStatus.ERROR);
    this.errorSubject.next('WebSocket connection error');
  }

  /**
   * Tenter de se reconnecter
   */
  private attemptReconnect(): void {
    if (this.reconnectAttempts >= environment.notification.wsMaxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;
    this.connectionStatusSubject.next(ConnectionStatus.RECONNECTING);

    console.log(
      `🔄 Reconnecting... (attempt ${this.reconnectAttempts}/${environment.notification.wsMaxReconnectAttempts})`
    );

    // La reconnexion est gérée automatiquement par STOMP client
    // Ce code est pour le tracking uniquement
  }

  /**
   * Générer un ID de souscription unique
   */
  private generateSubscriptionId(): string {
    return `sub-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  }

  ngOnDestroy(): void {
    this.disconnect();
  }
}
